import type { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { SchoolSubject, Teilleistungsschaechen } from '@chancenwerk/db/enums/enums.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import ImageClientsSupport from './../assets/Classroom-med-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';
import './anmeldung-component-formular-lkk-us.js';

/**
 * @customElement anmeldung-component-formular-support
 */
@customElement('anmeldung-component-formular-support')
export class AnmeldungPageRegistrationSupport extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';

  private _avoidStringOverflow(text: string, maxLen: number) {
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen) + '...';
  }

  override render() {
    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="md">
            <cr-text>
              Damit wir Ihr Kind bestmöglich unterstützen können, teilen Sie uns bitte mit, in welchen Fächern Ihr Kind
              von unserem Schulteam Unterstützung erhalten soll und ob bei Ihrem Kind Besonderheiten/Leistungsschwächen
              vorliegen, die das Lernen erschweren können. Bitte teilen Sie uns im Feld "Sonstige Hinweise" auch mit,
              wenn Ihr Kind nicht regelmäßig teilnehmen kann. Sie können uns diese Hinweise gerne auch telefonisch oder
              in einem persönlichen Gespräch mitteilen.
            </cr-text>
            <cr-flex space="xs">
              <cr-flex space="sm" horizontal crossaxis-center>
                <cr-dropdown-multiselection
                  ${this.form.field('funding', 'foerderbedarfLautEltern')}
                  placeholder=${this.translation.translate('childSupportNeeds')}
                  .selectables=${Object.values(SchoolSubject)}
                  .renderSelected=${(selected: Set<SchoolSubject>) =>
                    Array.from(selected)
                      .map(fach => fach)
                      .join(', ')}
                  ${css({ width: '100%', overflow: 'hidden' })}
                  @value-picked=${() => this.requestUpdate()}
                >
                </cr-dropdown-multiselection>
                <cr-icon
                  ${css({
                    padding: this.spacing.xs,

                    color: this.colors.accent.shade(500),
                    cursor: 'pointer',
                  })}
                >
                  info
                  <cr-tooltip clickable>
                    <cr-text ${css({ maxWidth: '400px' })}>
                      ${this.translation.translate('tooltips', 'further_instructions_from_parent')}
                    </cr-text>
                  </x-tooltip>
                </cr-icon>
              </cr-flex>
              ${
                this._size.mobile && this.student.value.funding.foerderbedarfLautEltern.length
                  ? html`
                      <cr-text tone="subtle" small ${css({ marginLeft: this.spacing.sm })}>
                        Ausgewählt: ${this.student.value.funding.foerderbedarfLautEltern.join(', ')}
                      </cr-text>
                    `
                  : nothing
              }
            </cr-flex>
            <cr-flex space="sm" horizontal crossaxis-center>
              <cr-input
                ${this.form.field('buts', 0, 'sonstigeFaecherTmp')}
                placeholder=${this.translation.translate('other_subjects')}
                ${css({ width: '100%' })}
              >
              </cr-input>
              <cr-icon
                ${css({
                  padding: this.spacing.xs,
                  color: this.colors.accent.shade(500),
                  cursor: 'pointer',
                })}
              >
                info
                <cr-tooltip clickable>
                  <cr-text ${css({ maxWidth: '400px' })}>
                    ${this.translation.translate('tooltips', 'further_instructions_other_lectures')}
                  </cr-text>
                </x-tooltip>
              </cr-icon>
            </cr-flex>
            <x-toggle-content label="Entbindung von der Schweigepflicht">
              <cr-text>
                Chancenwerk e.V. möchte Ihr Kind bestmöglich fördern. Dabei hilft dem Schulteam der Austausch mit den
                Lehrkräften Ihres Kindes im Rahmen einer pädagogischen Zusammenarbeit, z. B. bezogen auf (Zeugnis-)
                Noten, Lernstand, Sozialverhalten. Aus diesem Grund entbinden Sie die Verantwortlichen von Chancenwerk
                e.V. und die Lehrkräfte Ihres Kindes während der Laufzeit und zum Zwecke des Lernförderangebots von der
                Schweigepflicht. Sie können diese Entbindung von der Schweigepflicht jederzeit ohne Angabe von Gründen
                schriftlich widerrufen, z. B. per Brief oder E-Mail.
              </cr-text>
            </x-toggle-content>

            ${this._weakness()}

            <anmeldung-component-formular-lkk-us
              .form=${this.form}
              .translation=${this.translation}
              .student=${this.student}
              .schoolLevel=${this.schoolLevel}
              .choosenKPF=${this.choosenKPF}
            >
            </anmeldung-component-formular-lkk-us>

            <cr-text>
              Sie müssen Ihr Kind telefonisch entschuldigen, wenn es nicht zur Betreuung kommen kann. Dazu rufen Sie die
              Ansprechperson aus dem Schulteam an. Die Telefonnummer steht auf dem Chancenwerk-Infoblatt.
            </cr-text>

            <x-toggle-content label="Informationen zur Aufsichtspflicht und Erreichbarkeit">
              <cr-text>
                Die Aufsichtspflicht der Verantwortlichen von Chancenwerk e.V. beginnt und endet mit den bekannten
                Förderzeiten. Innerhalb der Aufsichtspflichtgrenzen muss den Anweisungen der Verantwortlichen Folge
                geleistet werden. Bei unerlaubtem Entfernen Ihres Kindes von der Gruppe oder unentschuldigtem Fehlen
                endet die Aufsichtspflicht der Verantwortlichen sofort. Die Aufsichtspflicht endet auch, wenn die
                Verantwortlichen Ihr Kind – bei Minderjährigen nach versuchter telefonischer Rücksprache – wegen
                Auffälligkeiten frühzeitig nach Hause schicken müssen. Sie verpflichten sich zu diesem Zweck, dem Verein
                aktuelle Telefonnummern mitzuteilen und achten darauf, dass eine zur Aufsicht Ihres Kindes befähigte
                Person während der Förderzeiten immer erreichbar ist.
              </cr-text>
            </x-toggle-content>
          </cr-flex>

          <cr-flex horizontal end crossaxis-center space="sm">
            <cr-button
              @click=${async () => {
                this.form.validate().catch(console.error);
              }}
            >
              ${this.translation.translate('next')}
            </cr-button>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _weakness() {
    return html`
      <cr-flex space="sm">
        <cr-flex space="xs" horizontal crossaxis-center>
          <cr-dropdown-multiselection
            ${this.form.field('funding', 'teilleistungsschwaechen')}
            placeholder=${this.translation.translate('performance_weaknesses')}
            .selectables=${Object.values(Teilleistungsschaechen)}
            .renderSelected=${(selected: Set<Teilleistungsschaechen>) => {
              return Array.from(selected)
                .map(s => this._avoidStringOverflow(s, 20))
                .join(', ');
            }}
            ${css({ width: '100%', overflow: 'hidden' })}
            @value-picked=${() => this.requestUpdate()}
          >
          </cr-dropdown-multiselection>
          <cr-icon
            ${css({
              padding: this.spacing.xs,
              color: this.colors.accent.shade(500),
              cursor: 'pointer',
            })}
          >
            info
            <cr-tooltip clickable>
              <cr-text ${css({ maxWidth: '400px' })}>
                ${this.translation.translate('tooltips', 'further_instructions_weaknesses')}
              </cr-text>
            </x-tooltip>
          </cr-icon>
        </cr-flex>
        ${
          this._size.mobile && this.student.value.funding.teilleistungsschwaechen.length
            ? html`
                <cr-text tone="subtle" small ${css({ marginLeft: this.spacing.sm })}>
                  Ausgewählt: ${this.student.value.funding.teilleistungsschwaechen.join(', ')}
                </cr-text>
              `
            : nothing
        }
      </cr-flex>
      <x-toggle-content label="Daten zu Leistungsschwächen">
        <cr-text>
          Mit Ihrer Unterschrift auf dem Formular, das wir Ihnen postalisch zusenden, willigen Sie ein, dass Chancenwerk
          e.V. im Rahmen des Lernförderangebots die Daten Ihres Kindes zu eventuellen Teilleistungsschwächen verarbeiten
          darf. Diese Einwilligung ist freiwillig. Sie können diese Einwilligung jederzeit mit Wirkung für die Zukunft
          widerrufen. Hierzu genügt ein schriftlicher Widerruf an Chancenwerk e.V., z. B. per E-Mail oder Brief.
        </cr-text>
      </x-toggle-content>
      <cr-flex space="sm" horizontal crossaxis-center>
        <cr-input
          ${this.form.field('funding', 'teilleistungsschwaechenSonst')}
          placeholder=${this.translation.translate('other_child_information')}
          ${css({ width: '100%' })}
        >
        </cr-input>
        <cr-icon
          ${css({
            padding: this.spacing.xs,

            color: this.colors.accent.shade(500),
            cursor: 'pointer',
          })}
        >
          info
          <cr-tooltip clickable>
            <cr-text ${css({ maxWidth: '400px' })}>
              ${this.translation.translate('tooltips', 'further_instructions_further_instruction')}
            </cr-text>
          </x-tooltip>
        </cr-icon>
      </cr-flex>
    `;
  }
}
